/**
 * Coaching Canvas Configuration
 *
 * Data sources, tools, outputs, and templates for the agent builder.
 * Tied to REAL backend agents and data — greyed-out items are not yet connected.
 *
 * Three Pillars: Acquisition, Development, Scouting
 * Every tool, source, and template aligns to a pillar for coach-friendly grouping.
 */

// ─── Pillar Configuration ────────────────────────────────────────────────────

export const PILLAR_CONFIG = {
  acquisition: {
    id: 'acquisition',
    label: 'Acquisition',
    shortLabel: 'ACQ',
    desc: 'Find and recruit players',
    color: '#00ff88',
    colorDim: '#00ff8830',
    icon: 'Search',
  },
  development: {
    id: 'development',
    label: 'Development',
    shortLabel: 'DEV',
    desc: 'Analyze film and track growth',
    color: '#0088FF',
    colorDim: '#0088FF30',
    icon: 'BarChart3',
  },
  scouting: {
    id: 'scouting',
    label: 'Scouting',
    shortLabel: 'SCT',
    desc: 'Break down opponents and generate intel',
    color: '#E8B800',
    colorDim: '#E8B80030',
    icon: 'Eye',
  },
  utility: {
    id: 'utility',
    label: 'Utility',
    shortLabel: 'UTL',
    desc: 'Export, communicate, and research',
    color: '#999999',
    colorDim: '#99999930',
    icon: 'Wrench',
  },
};

// ─── Data Sources ───────────────────────────────────────────────────────────
// `available: true` means we have real data flowing through the backend
// `vendorName` preserves the original vendor name for the Developer Portal

export const DATA_SOURCES = [
  // Baseball-specific
  { id: 'statcast', name: 'Pitch Tracking', vendorName: 'Statcast', icon: 'Target', category: 'baseball', pillar: 'scouting', desc: 'MLB pitch tracking & batted ball data via BigQuery', available: true },
  { id: 'trackman', name: 'Ball Flight Sensors', vendorName: 'TrackMan', icon: 'Radio', category: 'baseball', pillar: 'scouting', desc: 'Ball flight & spin rate metrics', available: false },
  { id: 'savant', name: 'Advanced Sabermetrics', vendorName: 'Baseball Savant', icon: 'BarChart3', category: 'baseball', pillar: 'scouting', desc: 'Advanced MLB sabermetrics', available: false },

  // Basketball-specific
  { id: 'synergy', name: 'Play-by-Play Analytics', vendorName: 'Synergy Sports', icon: 'Circle', category: 'basketball', pillar: 'scouting', desc: 'Play-by-play analytics (994 possessions in BQ)', available: true },
  { id: 'shotquality', name: 'Shot Value Models', vendorName: 'Shot Quality', icon: 'Target', category: 'basketball', pillar: 'development', desc: 'Expected shot value models', available: false },

  // Multi-sport (connected)
  { id: 'video', name: 'Video Analysis', vendorName: 'Video Analysis', icon: 'Video', category: 'multi', pillar: 'development', desc: 'AI-analyzed game & practice film via Gemini 3', available: true },
  { id: 'scouting', name: 'Scouting Reports', vendorName: 'Scouting Reports', icon: 'Clipboard', category: 'multi', pillar: 'scouting', desc: 'AI-generated scouting evaluations', available: true },
  { id: 'roster', name: 'Roster Data', vendorName: 'Roster Data', icon: 'Users', category: 'multi', pillar: 'development', desc: 'Player profiles, stats & history via NatStat', available: true },
  { id: 'natstat', name: 'NatStat', vendorName: 'NatStat', icon: 'Database', category: 'multi', pillar: 'scouting', desc: 'National statistics database (33 leagues, 6 sports)', available: true },

  // Multi-sport (not connected)
  { id: 'hawkeye', name: 'Ball & Player Tracking', vendorName: 'Hawk-Eye', icon: 'Eye', category: 'multi', pillar: 'scouting', desc: 'Ball tracking & player positioning', available: false },
  { id: 'catapult', name: 'Wearable GPS', vendorName: 'Catapult', icon: 'Watch', category: 'multi', pillar: 'development', desc: 'Player load & movement data', available: false },
  { id: 'wearables', name: 'Wearable Data', vendorName: 'Wearable Data', icon: 'Watch', category: 'multi', pillar: 'development', desc: 'Biometric & movement sensors', available: false },

  // Business
  { id: 'portal', name: 'Transfer Portal', vendorName: 'Transfer Portal', icon: 'Users', category: 'business', pillar: 'acquisition', desc: 'Transfer portal entries & commitments', available: true },
  { id: 'nil', name: 'NIL Valuations', vendorName: 'NIL Valuations', icon: 'DollarSign', category: 'business', pillar: 'acquisition', desc: 'Name, Image, Likeness market data', available: false },

  // Infrastructure
  { id: 'bigquery', name: 'BigQuery', vendorName: 'BigQuery', icon: 'Database', category: 'infra', pillar: 'utility', desc: '507 segments, 994 possessions, 487 shots in warehouse', available: true },
  { id: 'upload', name: 'Custom Upload', vendorName: 'Custom Upload', icon: 'Upload', category: 'infra', pillar: 'utility', desc: 'Upload CSV, PDF, or video files', available: true },
  { id: 'web', name: 'Web Research', vendorName: 'Web Research', icon: 'Globe', category: 'infra', pillar: 'utility', desc: 'Web search for scouting intel & news', available: true },
];

// ─── Agent Tools (mapped to real backend skill handlers) ────────────────────
// `category` is sport-based: 'baseball', 'basketball', or 'multi' (all sports)

export const AGENT_TOOLS = [
  // Scouting pillar — opponent intel & data queries
  { id: 'english-to-sql', name: 'Natural Language Query', icon: 'MessageSquare', desc: 'Ask questions in plain English → BigQuery SQL', category: 'multi', pillar: 'scouting', available: true },
  { id: 'query-statcast', name: 'Statcast Query', icon: 'Target', desc: 'Query pitch tracking & batted ball data', category: 'baseball', pillar: 'scouting', available: true },
  { id: 'analyze-pitcher', name: 'Pitcher Analyzer', icon: 'Target', desc: 'Break down pitch sequences & arsenal', category: 'baseball', pillar: 'scouting', available: true },
  { id: 'pitch-tipping-analysis', name: 'Advanced Scouting', icon: 'Eye', desc: 'Detect exploitable pitcher tells from video', category: 'baseball', pillar: 'scouting', available: true },
  { id: 'get-team-stats', name: 'Team Stats', icon: 'BarChart3', desc: 'Aggregate team performance data', category: 'multi', pillar: 'scouting', available: true },

  // Development pillar — player growth & film
  { id: 'analyze-game-film', name: 'Film Analyzer', icon: 'Video', desc: 'AI analysis of game film with Gemini 3 Flash', category: 'multi', pillar: 'development', available: true },
  { id: 'get-player-stats', name: 'Player Stats', icon: 'BarChart3', desc: 'Retrieve detailed player statistics', category: 'multi', pillar: 'development', available: true },
  { id: 'analyze-performance-trends', name: 'Trend Analysis', icon: 'BarChart3', desc: 'Track performance changes over time', category: 'multi', pillar: 'development', available: true },
  { id: 'comparative-analysis', name: 'Matchup Engine', icon: 'Users', desc: 'Head-to-head player comparisons', category: 'multi', pillar: 'development', available: true },

  // Acquisition pillar — recruiting & portal
  { id: 'player-lookup', name: 'Player Lookup', icon: 'Search', desc: 'Find player profiles and career data', category: 'multi', pillar: 'acquisition', available: true },
  { id: 'search-similar-players', name: 'Player Comps', icon: 'Users', desc: 'Find statistically similar players', category: 'multi', pillar: 'acquisition', available: true },
  { id: 'search-transfer-portal', name: 'Portal Search', icon: 'Search', desc: 'Search transfer portal by criteria', category: 'multi', pillar: 'acquisition', available: true },
  { id: 'generate-scouting-report', name: 'Scout Report Gen', icon: 'FileText', desc: 'AI-generated scouting reports', category: 'multi', pillar: 'acquisition', available: true },
  { id: 'scouting-report', name: 'Scouting Report', icon: 'Clipboard', desc: 'Comprehensive prospect evaluation', category: 'multi', pillar: 'acquisition', available: true },

  // Utility pillar — export, comms, research
  { id: 'generate-pdf', name: 'PDF Export', icon: 'FileDown', desc: 'Generate formatted PDF reports', category: 'multi', pillar: 'utility', available: true },
  { id: 'send-email', name: 'Send Email', icon: 'Mail', desc: 'Email reports and alerts to staff', category: 'multi', pillar: 'utility', available: true },
  { id: 'video-clip-extract', name: 'Video Clipper', icon: 'Scissors', desc: 'Extract video highlights', category: 'multi', pillar: 'utility', available: true },
  { id: 'get-espn-news', name: 'ESPN News', icon: 'Globe', desc: 'Latest sports news and updates', category: 'multi', pillar: 'utility', available: true },
  { id: 'get-espn-scores', name: 'Live Scores', icon: 'Monitor', desc: 'Current scores and standings', category: 'multi', pillar: 'utility', available: true },
];

// ─── Output Channels ────────────────────────────────────────────────────────

export const OUTPUT_CHANNELS = [
  { id: 'chat', name: 'Chat Response', icon: 'MessageSquare', pillar: 'utility', available: true },
  { id: 'pdf', name: 'PDF Export', icon: 'FileDown', pillar: 'utility', available: true },
  { id: 'email', name: 'Email Report', icon: 'Mail', pillar: 'utility', available: true },
  { id: 'dashboard', name: 'Live Dashboard', icon: 'Monitor', pillar: 'development', available: false },
  { id: 'slack', name: 'Slack Alert', icon: 'MessageSquare', pillar: 'utility', available: false },
  { id: 'sheets', name: 'Google Sheets', icon: 'BarChart3', pillar: 'development', available: false },
  { id: 'video-out', name: 'Video Package', icon: 'Film', pillar: 'scouting', available: true },
  { id: 'mobile-push', name: 'Mobile Push', icon: 'Smartphone', pillar: 'utility', available: false },
  { id: 'api', name: 'API Endpoint', icon: 'Plug', pillar: 'utility', available: false },
];

// ─── Sport-Specific Agent Templates (mapped to REAL backend agent IDs) ──────

export const AGENT_TEMPLATES = [
  // Basketball — REAL agents
  {
    id: 'basketball_analyst',
    agentId: 'basketball_analyst',
    name: 'Basketball Analyst',
    sport: 'basketball',
    pillar: 'scouting',
    icon: 'Circle',
    desc: 'Comprehensive basketball analysis with video, transfer portal, scouting, and NL2SQL data queries',
    realTools: ['player-lookup', 'player-search', 'english-to-sql', 'comparative-analysis', 'scouting-report', 'get-player-stats', 'get-team-stats', 'analyze-performance-trends', 'search-similar-players', 'search-transfer-portal', 'analyze-game-film', 'generate-scouting-report', 'send-email'],
    sources: ['synergy', 'bigquery', 'roster', 'natstat', 'video', 'portal'],
    starterPrompts: [
      'What were our shooting percentages from the paint last game?',
      'Find guards in the transfer portal who shoot 38%+ from three',
      'Compare our top 5 scorers to conference averages',
      'Generate a scouting report for our next opponent',
    ],
  },
  {
    id: 'basketball_video_analyst',
    agentId: 'basketball_video_analyst',
    name: 'Basketball Film Room',
    sport: 'basketball',
    pillar: 'development',
    icon: 'Video',
    desc: 'Deep game film analysis, play breakdown, and video clip extraction',
    realTools: ['analyze-game-film', 'video-clip-extract', 'player-lookup', 'comparative-analysis'],
    sources: ['video', 'synergy', 'bigquery'],
    starterPrompts: [
      'Analyze our transition defense breakdowns from the last game',
      'Show me our pick-and-roll coverage in the second half',
      'Extract clips of our best fast break possessions',
    ],
  },
  {
    id: 'human_in_the_loop',
    agentId: 'human_in_the_loop',
    name: 'Basketball Analytics Hub',
    sport: 'basketball',
    pillar: 'acquisition',
    icon: 'BarChart3',
    desc: 'Basketball analytics, transfer portal search, and recruiting intelligence',
    realTools: ['player-lookup', 'player-search', 'english-to-sql', 'comparative-analysis', 'scouting-report', 'send-email'],
    sources: ['natstat', 'roster', 'portal', 'bigquery'],
    starterPrompts: [
      'Search the transfer portal for big men averaging 8+ rebounds',
      'Email me a scouting report on the top 3 available forwards',
      'What are the NL2SQL stats for Wembanyama vs SGA?',
    ],
  },

  // Baseball — REAL agents
  {
    id: 'baseball_analyst',
    agentId: 'baseball_analyst',
    name: 'Baseball Analyst',
    sport: 'baseball',
    pillar: 'scouting',
    icon: 'Target',
    desc: 'Comprehensive baseball analysis with Statcast, pitching mechanics, scouting, and video',
    realTools: ['player-lookup', 'player-search', 'english-to-sql', 'comparative-analysis', 'scouting-report', 'get-player-stats', 'get-team-stats', 'analyze-performance-trends', 'search-similar-players', 'pitch-tipping-analysis', 'biomechanics-report', 'query-statcast', 'analyze-pitcher', 'analyze-baseball-film', 'generate-scouting-report', 'web-search', 'get-current-news', 'statcast-query'],
    sources: ['statcast', 'bigquery', 'video', 'roster', 'natstat', 'scouting', 'web'],
    starterPrompts: [
      'Analyze our starter\'s pitch usage against left-handed hitters',
      'Query Statcast for pitch velocity trends this season',
      'Generate a full scouting report with MLB comps',
      'What are the latest trade rumors for our division?',
    ],
  },
  {
    id: 'opponent-scout',
    agentId: 'opponent-scout',
    name: 'Opponent Scout',
    sport: 'baseball',
    pillar: 'scouting',
    icon: 'Eye',
    desc: 'Deep pitcher analysis, tell detection, and biomechanics reporting',
    realTools: ['pitch-tipping-analysis', 'biomechanics-report', 'video-clip-extract', 'player-lookup'],
    sources: ['video', 'statcast', 'bigquery'],
    starterPrompts: [
      'Detect pitch tipping patterns for the opposing starter',
      'Generate a biomechanics report on their closer',
      'Extract clips of suspicious mechanical tells',
    ],
  },
  {
    id: 'at-bat-strategy',
    agentId: 'at-bat-strategy',
    name: 'At-Bat Strategist',
    sport: 'baseball',
    pillar: 'development',
    icon: 'Clipboard',
    desc: 'Count-specific exploitation strategies and matchup advantages',
    realTools: ['player-lookup', 'comparative-analysis', 'scouting-report'],
    sources: ['statcast', 'roster', 'scouting'],
    starterPrompts: [
      'What pitches should we attack in 2-0 counts vs this pitcher?',
      'Build an at-bat strategy for our 3-hole hitter vs their starter',
      'Compare our lineup\'s success rates by pitch type',
    ],
  },
  {
    id: 'game-prep',
    agentId: 'game-prep',
    name: 'Game Prep Agent',
    sport: 'baseball',
    pillar: 'utility',
    icon: 'FileText',
    desc: 'Pre-game briefing materials, PDF reports, and email distribution',
    realTools: ['generate-pdf', 'generate-pptx', 'send-email', 'pitch-tipping-synthesis'],
    sources: ['statcast', 'scouting', 'roster'],
    starterPrompts: [
      'Generate a pre-game briefing PDF for tomorrow\'s game',
      'Email the coaching staff a pitcher scouting report',
      'Create a pitch tipping synthesis for our hitters\' meeting',
    ],
  },

  // Soccer — uses sports-analyst backend agent
  {
    id: 'soccer_analyst',
    agentId: 'sports-analyst',
    name: 'Soccer Analyst',
    sport: 'soccer',
    pillar: 'scouting',
    icon: 'Circle',
    desc: 'Tactical analysis, xG models, player performance tracking, and match intelligence across leagues',
    realTools: ['player-lookup', 'player-search', 'comparative-analysis', 'get-player-stats', 'get-team-stats', 'analyze-performance-trends', 'search-similar-players', 'scouting-report'],
    sources: ['natstat', 'roster', 'video', 'web'],
    starterPrompts: [
      'Compare our midfield possession stats to the league average',
      'Find forwards with 10+ goals who are available in the transfer window',
      'Analyze our pressing efficiency in the first 15 minutes',
      'Generate a scouting report on our next opponent\'s set pieces',
    ],
  },
  {
    id: 'soccer_scout',
    agentId: 'sports-analyst',
    name: 'Transfer & Recruitment Agent',
    sport: 'soccer',
    pillar: 'acquisition',
    icon: 'Search',
    desc: 'Player recruitment, transfer market intelligence, and fit scoring for squad building',
    realTools: ['player-lookup', 'player-search', 'comparative-analysis', 'scouting-report', 'generate-scouting-report', 'search-similar-players', 'send-email'],
    sources: ['natstat', 'roster', 'web', 'scouting'],
    starterPrompts: [
      'Find left-backs under 23 with high crossing accuracy',
      'Generate a transfer target shortlist for our defensive midfield need',
      'Compare this prospect to our current starters statistically',
    ],
  },

  // Golf — uses sports-analyst backend agent
  {
    id: 'golf_analyst',
    agentId: 'sports-analyst',
    name: 'Golf Performance Agent',
    sport: 'golf',
    pillar: 'development',
    icon: 'Target',
    desc: 'Strokes gained analysis, course strategy, player development tracking, and tournament preparation',
    realTools: ['player-lookup', 'get-player-stats', 'comparative-analysis', 'analyze-performance-trends', 'search-similar-players', 'scouting-report'],
    sources: ['natstat', 'roster', 'web'],
    starterPrompts: [
      'Analyze our team\'s strokes gained putting vs conference average',
      'Which players are trending up in driving accuracy this season?',
      'Compare our top 5 players to the field at the conference championship',
      'What course strategy adjustments should we make for this week\'s tournament?',
    ],
  },

  // Hockey — uses sports-analyst backend agent
  {
    id: 'hockey_analyst',
    agentId: 'sports-analyst',
    name: 'Hockey Analyst',
    sport: 'hockey',
    pillar: 'scouting',
    icon: 'Circle',
    desc: 'Corsi/Fenwick analytics, line optimization, power play/penalty kill analysis, and game intelligence',
    realTools: ['player-lookup', 'player-search', 'get-player-stats', 'get-team-stats', 'comparative-analysis', 'analyze-performance-trends', 'search-similar-players', 'scouting-report'],
    sources: ['natstat', 'roster', 'video', 'web'],
    starterPrompts: [
      'What are our Corsi percentages by line combination?',
      'Analyze our power play efficiency over the last 10 games',
      'Find defensemen in the portal with strong shot suppression metrics',
      'Compare our goaltender to conference leaders in save percentage',
    ],
  },

  // Football — uses sports-analyst backend agent
  {
    id: 'football_analyst',
    agentId: 'sports-analyst',
    name: 'Football Analyst',
    sport: 'football',
    pillar: 'scouting',
    icon: 'Circle',
    desc: 'Formation analysis, play-calling tendencies, player evaluation, and opponent scouting for football',
    realTools: ['player-lookup', 'player-search', 'get-player-stats', 'get-team-stats', 'comparative-analysis', 'analyze-performance-trends', 'scouting-report', 'analyze-game-film'],
    sources: ['natstat', 'roster', 'video', 'web'],
    starterPrompts: [
      'Analyze our offensive formation tendencies by down and distance',
      'Compare our QB\'s EPA per play to conference averages',
      'Find edge rushers in the transfer portal with high pressure rates',
      'Generate a defensive game plan based on opponent tendencies',
    ],
  },
  {
    id: 'football_scout',
    agentId: 'sports-analyst',
    name: 'Football Scout',
    sport: 'football',
    pillar: 'acquisition',
    icon: 'Eye',
    desc: 'Transfer portal and recruiting intelligence for football with film evaluation support',
    realTools: ['player-lookup', 'player-search', 'comparative-analysis', 'scouting-report', 'generate-scouting-report', 'search-transfer-portal', 'send-email'],
    sources: ['natstat', 'roster', 'portal', 'scouting', 'web'],
    starterPrompts: [
      'Search the transfer portal for offensive linemen with Power 5 experience',
      'Generate a scouting report on this DB recruit',
      'Email the staff a ranked list of available WR targets',
    ],
  },

  // Multi-sport — REAL agents
  {
    id: 'sports-analyst',
    agentId: 'sports-analyst',
    name: 'Sports Analytics Agent',
    sport: 'all',
    pillar: 'scouting',
    icon: 'BarChart3',
    desc: 'Multi-sport analytics for any coach — player stats, trends, comparisons, and standings across 33 leagues',
    realTools: ['player-lookup', 'player-search', 'comparative-analysis', 'scouting-report', 'get-player-stats', 'get-team-stats', 'analyze-performance-trends', 'get-standings', 'search-similar-players'],
    sources: ['natstat', 'roster', 'bigquery'],
    starterPrompts: [
      'Show me the top scorers in our conference this season',
      'Compare our team stats to the league average',
      'Find players statistically similar to our star player',
      'What are the current standings in our division?',
    ],
  },
  {
    id: 'adk-email-agent',
    agentId: 'adk-email-agent',
    name: 'Email & Reports Agent',
    sport: 'all',
    pillar: 'utility',
    icon: 'Mail',
    desc: 'Email drafting, PDF report generation, and communication distribution',
    realTools: ['send-email', 'generate-pdf'],
    sources: ['roster', 'scouting'],
    starterPrompts: [
      'Draft an email to the recruiting coordinator about top prospects',
      'Generate a PDF scouting report for our AD meeting',
      'Send the weekly performance summary to coaching staff',
    ],
  },
  {
    id: 'research-agent',
    agentId: 'sports-analyst',
    name: 'Research Agent',
    sport: 'all',
    pillar: 'utility',
    icon: 'Globe',
    desc: 'Deep web research, news aggregation, and intelligence gathering for any sport or topic',
    realTools: ['get-espn-news', 'get-espn-scores', 'player-lookup', 'player-search', 'comparative-analysis', 'generate-pdf', 'send-email'],
    sources: ['web', 'natstat', 'roster'],
    starterPrompts: [
      'Research the latest coaching hires in our conference',
      'Find news about transfer portal trends this cycle',
      'Aggregate recruiting rankings for our sport from multiple sources',
      'Build a competitive intelligence report on our rival program',
    ],
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

export function getTemplatesForSport(sportId) {
  return AGENT_TEMPLATES.filter(
    t => t.sport === sportId || t.sport === 'all'
  );
}

export function getSourcesForSport(sportId) {
  return DATA_SOURCES.filter(s => {
    if (s.category === 'multi' || s.category === 'business' || s.category === 'infra') return true;
    return s.category === sportId;
  });
}

export function getToolsForSport(sportId) {
  return AGENT_TOOLS.filter(t => {
    if (t.category === 'multi') return true;
    return t.category === sportId;
  });
}

/** Group items by pillar, returning { acquisition: [...], development: [...], scouting: [...], utility: [...] } */
export function groupByPillar(items) {
  const groups = { acquisition: [], development: [], scouting: [], utility: [] };
  for (const item of items) {
    const p = item.pillar || 'utility';
    if (groups[p]) groups[p].push(item);
    else groups.utility.push(item);
  }
  return groups;
}

/** Get the pillar color for an item */
export function getPillarColor(pillarId) {
  return PILLAR_CONFIG[pillarId]?.color || PILLAR_CONFIG.utility.color;
}

export const SPORT_OPTIONS = [
  { id: 'basketball', name: 'Basketball', emoji: '\uD83C\uDFC0', hasRealAgents: true },
  { id: 'baseball', name: 'Baseball', emoji: '\u26BE', hasRealAgents: true },
  { id: 'soccer', name: 'Soccer', emoji: '\u26BD', hasRealAgents: true },
  { id: 'hockey', name: 'Hockey', emoji: '\uD83C\uDFD2', hasRealAgents: true },
  { id: 'football', name: 'Football', emoji: '\uD83C\uDFC8', hasRealAgents: true },
  { id: 'golf', name: 'Golf', emoji: '\u26F3', hasRealAgents: true },
];

export const SOURCE_CATEGORIES = {
  baseball: 'Baseball',
  basketball: 'Basketball',
  soccer: 'Soccer',
  golf: 'Golf',
  hockey: 'Hockey',
  football: 'Football',
  multi: 'Universal',
  business: 'Business',
  infra: 'Infrastructure',
};

export const TOOL_CATEGORIES = {
  baseball: 'Baseball',
  basketball: 'Basketball',
  multi: 'Universal',
};

// ─── Default Starter Prompts by Sport ─────────────────────────────────────
export const DEFAULT_PROMPTS = {
  basketball: [
    'What were our shooting percentages from the paint last game?',
    'Find guards in the transfer portal who shoot 38%+ from three',
    'Compare our top 5 scorers to conference averages',
    'Generate a scouting report for our next opponent',
  ],
  baseball: [
    "Analyze our starter's pitch usage against left-handed hitters",
    'Query Statcast for pitch velocity trends this season',
    'Generate a full scouting report with MLB comps',
    'What are the latest trade rumors for our division?',
  ],
  soccer: [
    'Compare our midfield possession stats to the league average',
    'Find forwards with 10+ goals available in the transfer window',
    'Analyze our pressing efficiency in the first 15 minutes',
    "Generate a scouting report on our next opponent's set pieces",
  ],
  hockey: [
    'What are our Corsi percentages by line combination?',
    'Analyze our power play efficiency over the last 10 games',
    'Find defensemen in the portal with strong shot suppression metrics',
    'Compare our goaltender to conference leaders in save percentage',
  ],
  football: [
    'Analyze our offensive formation tendencies by down and distance',
    "Compare our QB's EPA per play to conference averages",
    'Find edge rushers in the transfer portal with high pressure rates',
    'Generate a defensive game plan based on opponent tendencies',
  ],
  golf: [
    "Analyze our team's strokes gained putting vs conference average",
    'Which players are trending up in driving accuracy this season?',
    'Compare our top 5 players to the field at the conference championship',
    'What course strategy adjustments should we make for this week?',
  ],
};

// ─── Semantic Builder Prompts ─────────────────────────────────────────────

export const BUILDER_SYSTEM_PROMPT = `You are a SLAM Sports AI agent architect. When a coach or analyst describes what kind of agent they want, you:

1. Identify the sport context and use case
2. Recommend which data sources to connect (from our available: Statcast, Synergy, NatStat, BigQuery, Video Analysis, Roster Data, Transfer Portal, Web Research, Custom Upload)
3. Recommend which tools to enable (from our available: NL2SQL, Film Analyzer, Matchup Engine, Player Lookup, Trend Analysis, Portal Search, Scout Report, PDF Export, Email, Video Clipper, ESPN News)
4. Suggest a name and description for the agent
5. Generate 3-4 starter prompts the agent should handle well

Respond in JSON format:
{
  "name": "Agent Name",
  "description": "What this agent does",
  "sport": "basketball|baseball|soccer|golf|hockey|football|all",
  "sources": ["source-id-1", "source-id-2"],
  "tools": ["tool-id-1", "tool-id-2"],
  "outputs": ["chat", "pdf", "email"],
  "starterPrompts": ["prompt1", "prompt2", "prompt3"],
  "reasoning": "Why this configuration was chosen"
}`;

export const BUILDER_EXAMPLES = [
  {
    input: 'I need an agent that can help me prepare for our next basketball game',
    output: {
      name: 'Game Prep Assistant',
      sport: 'basketball',
      sources: ['synergy', 'bigquery', 'roster', 'video'],
      tools: ['english-to-sql', 'analyze-game-film', 'comparative-analysis', 'player-lookup', 'generate-pdf'],
      outputs: ['chat', 'pdf'],
    },
  },
  {
    input: 'Build me a research agent that can find transfer portal targets for soccer',
    output: {
      name: 'Soccer Transfer Scout',
      sport: 'soccer',
      sources: ['natstat', 'roster', 'portal', 'web'],
      tools: ['player-lookup', 'player-search', 'comparative-analysis', 'scouting-report', 'search-transfer-portal', 'send-email'],
      outputs: ['chat', 'email', 'pdf'],
    },
  },
];

// ─── Tooltip Tour Steps ─────────────────────────────────────────────────────

export const CONFIGURE_TOUR_STEPS = [
  {
    targetSelector: '[data-tour="agent-identity"]',
    title: 'Name Your Agent',
    body: 'Give your agent a name and describe what it should do — like naming a coaching position on your staff.',
    placement: 'bottom',
  },
  {
    targetSelector: '[data-tour="pillar-acquisition"]',
    title: 'Player Acquisition',
    body: 'These tools help you find and recruit players — portal search, scouting reports, and player comparisons.',
    placement: 'bottom',
  },
  {
    targetSelector: '[data-tour="pillar-development"]',
    title: 'Player Development',
    body: 'Track player growth with film analysis, performance trends, and matchup breakdowns.',
    placement: 'bottom',
  },
  {
    targetSelector: '[data-tour="pillar-scouting"]',
    title: 'Opponent Scouting',
    body: 'Break down opponents with data queries, pitch analysis, and team stats.',
    placement: 'bottom',
  },
  {
    targetSelector: '[data-tour="source-available"]',
    title: 'Connected Sources',
    body: 'Highlighted items are live and connected to real data. Greyed items are coming soon.',
    placement: 'right',
  },
  {
    targetSelector: '[data-tour="config-summary"]',
    title: 'Agent Summary',
    body: 'Your agent\'s configuration at a glance — sources, tools, outputs, and the AI model powering it.',
    placement: 'top',
  },
  {
    targetSelector: '[data-tour="deploy-button"]',
    title: 'Deploy Your Agent',
    body: 'When you\'re ready, deploy your agent to start using it. You\'ll be guided through a few setup questions.',
    placement: 'left',
  },
];

export const BUILD_TOUR_STEPS = [
  {
    targetSelector: '[data-tour="node-palette"]',
    title: 'Node Palette',
    body: 'Drag these blocks onto the canvas to build your agent\'s workflow. They\'re grouped by function.',
    placement: 'right',
  },
  {
    targetSelector: '[data-tour="flow-canvas"]',
    title: 'Agent Canvas',
    body: 'This is your workspace. Connect blocks by dragging between the gold handles to define your agent\'s flow.',
    placement: 'bottom',
  },
  {
    targetSelector: '[data-tour="start-node"]',
    title: 'Start Trigger',
    body: 'Every agent starts here. This defines what kicks off the workflow — a question, a schedule, or an event.',
    placement: 'right',
  },
  {
    targetSelector: '[data-tour="agent-node"]',
    title: 'AI Brain',
    body: 'This is the AI reasoning engine. It decides what tools to use and how to respond based on your instructions.',
    placement: 'bottom',
  },
  {
    targetSelector: '[data-tour="pillar-colors"]',
    title: 'Color Guide',
    body: 'Green = Acquisition, Blue = Development, Gold = Scouting. Colors help you see your agent\'s focus at a glance.',
    placement: 'left',
  },
  {
    targetSelector: '[data-tour="flow-controls"]',
    title: 'Canvas Controls',
    body: 'Zoom in/out, fit to screen, and lock the canvas. Use the minimap to navigate large workflows.',
    placement: 'top',
  },
];
